import * as React from 'react';
import {logError} from '../utils/log-error';

interface Props<
    Value extends string,
    ChangeEntityParams extends Array<unknown>,
    RewriteKeyParams extends Array<unknown>
> {
    // these params remove previous key in storage when they change
    rewriteKeyParams: RewriteKeyParams;
    // these params keep previous key in storage when they change
    changeEntityParams: ChangeEntityParams;
    // make sure this function doesn't change ever
    getKey: (
        changeEntityParams: ChangeEntityParams,
        rewriteKeyParams: RewriteKeyParams
    ) => string | undefined;
    defaultValue: Value;
    validateValue?: (value: Value) => boolean;
    debug?: boolean;

    // upgrade this version between app releases if the format of the stored value changes and previous
    // value stored in user's ls can break new version of the app;
    // for it to function properly, always upgrade the version to be prevVersion + 1
    valueVersion?: number;
}

/* eslint-disable no-console */
export function useLsValue<
    Value extends string,
    ChangeEntityParams extends Array<unknown>,
    RewriteKeyParams extends Array<unknown>
>(
    props: Props<Value, ChangeEntityParams, RewriteKeyParams>
): [Value, (arg: Value) => void, () => void] {
    const {
        getKey,
        changeEntityParams,
        rewriteKeyParams,
        defaultValue,
        validateValue,
        valueVersion = 0
    } = props;
    const [valueUpdatedTimes, setValueUpdatedTimes] = React.useState<number>(0);
    const previousRewriteKeyParamsRef = React.useRef(rewriteKeyParams);
    const previousChangeEntityParamsRef = React.useRef(changeEntityParams);

    const getVersionedKey = React.useCallback(
        (
            chEntityParams: ChangeEntityParams,
            rKeyParams: RewriteKeyParams,
            version: number = valueVersion
        ) => {
            const key = getKey(chEntityParams, rKeyParams);

            if (version === 0) {
                return key;
            }

            return key ? `${key}-v${version}` : undefined;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const value: Value | undefined = React.useMemo(() => {
        const key = getVersionedKey(changeEntityParams, rewriteKeyParams);
        if (props.debug) {
            console.log(`getting new value for key ${key}`);
        }

        if (!key) {
            return undefined;
        }

        const val = localStorage.getItem(key) as Value;
        if (props.debug) {
            console.log('new value from ls: ', val);
        }
        const isInvalidValue = val && validateValue && !validateValue(val);
        if (val && !isInvalidValue) {
            if (props.debug) {
                console.log('returned val from ls');
            }
            return val;
        }

        if (isInvalidValue) {
            if (props.debug) {
                console.error(
                    new Error(`Invalid localstorage value for key ${key}, provided value: ${val}`)
                );
            }
            logError(
                new Error(`Invalid localstorage value for key ${key}, provided value: ${val}`)
            );
            if (process.env.NODE_ENV === 'test') {
                throw new Error(
                    `Invalid localstorage value for key ${key}, provided value: ${val}`
                );
            }
        }
        if (props.debug) {
            console.log(`returning default value ${defaultValue}`);
        }

        return defaultValue;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...changeEntityParams, ...rewriteKeyParams, valueUpdatedTimes, defaultValue, valueVersion]);

    React.useEffect(() => {
        const rewriteKeyParamsChanged =
            rewriteKeyParams.toString() !== previousRewriteKeyParamsRef.current.toString();
        const changeEntityParamsChanged =
            changeEntityParams.toString() !== previousChangeEntityParamsRef.current.toString();
        const previousKey = getVersionedKey(
            previousChangeEntityParamsRef.current,
            previousRewriteKeyParamsRef.current
        );

        if (rewriteKeyParamsChanged && !changeEntityParamsChanged && previousKey) {
            localStorage.removeItem(previousKey);
        }

        previousRewriteKeyParamsRef.current = rewriteKeyParams;
        previousChangeEntityParamsRef.current = changeEntityParams;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...rewriteKeyParams, ...changeEntityParams]);

    const setValue = React.useCallback(
        (val: Value) => {
            const key = getVersionedKey(changeEntityParams, rewriteKeyParams);

            if (!key) {
                if (props.debug) {
                    console.log('tried to set ls value but no key');
                }
                return;
            }

            if (props.debug) {
                console.log(`ls value for key ${key} successfully set: ${val}`);
            }
            localStorage.setItem(key, val);
            setValueUpdatedTimes((prevState) => prevState + 1);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [...changeEntityParams, ...rewriteKeyParams]
    );

    const clearValue = React.useCallback(() => {
        const key = getVersionedKey(changeEntityParams, rewriteKeyParams);

        if (!key) {
            return;
        }

        if (props.debug) {
            console.log(`cleared ls value for key ${key}`);
        }

        localStorage.removeItem(key);
        setValueUpdatedTimes((prevState) => prevState + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...changeEntityParams, ...rewriteKeyParams]);

    React.useEffect(() => {
        if (valueVersion !== 0) {
            const prevVersionKey = getVersionedKey(
                changeEntityParams,
                rewriteKeyParams,
                valueVersion - 1
            );

            if (prevVersionKey) {
                localStorage.removeItem(prevVersionKey);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return [value || defaultValue, setValue, clearValue];
}
